import React, { useState, useEffect } from 'react';
import { Clock, TrendingUp, X, Search } from 'lucide-react';
import { searchHistoryAPI } from '../services/api';

const SearchHistory = ({ 
  onSearchSelect, 
  cemeteryId = null,
  maxRecent = 5,
  maxPopular = 5,
  className = "" 
}) => {
  const [recentSearches, setRecentSearches] = useState([]); // 🔧 빈 배열로 초기화
  const [popularSearches, setPopularSearches] = useState([]); // 🔧 빈 배열로 초기화
  const [isLoading, setIsLoading] = useState(false);

  // 검색 히스토리 로드
  const loadSearchHistory = async () => {
    console.log('🔍 SearchHistory: 검색 히스토리 로드 시작');
    setIsLoading(true);
    try {
      // 최근 검색어 로드
      console.log('🔍 SearchHistory: 최근 검색어 API 호출');
      const recentResponse = await searchHistoryAPI.getRecent({ limit: maxRecent });
      console.log('🔍 SearchHistory: 최근 검색어 응답:', recentResponse);
      console.log('🔍 SearchHistory: recentResponse.data:', recentResponse.data);
      console.log('🔍 SearchHistory: recentResponse.data.data:', recentResponse.data?.data);
      // 🔧 API 응답 구조에 맞게 데이터 접근
      const recentData = recentResponse.data?.data || recentResponse.data || [];
      console.log('🔍 SearchHistory: 최근 검색어 데이터 설정:', recentData);
      setRecentSearches(recentData);
      console.log('🔍 SearchHistory: 최근 검색어 상태 업데이트 완료, 길이:', recentData.length);

      // 인기 검색어 로드
      console.log('🔍 SearchHistory: 인기 검색어 API 호출');
      let popularResponse;
      if (cemeteryId) {
        popularResponse = await searchHistoryAPI.getCemeteryPopular(cemeteryId, { limit: maxPopular });
      } else {
        popularResponse = await searchHistoryAPI.getPopular({ limit: maxPopular });
      }
      console.log('🔍 SearchHistory: 인기 검색어 응답:', popularResponse);
      console.log('🔍 SearchHistory: popularResponse.data:', popularResponse.data);
      console.log('🔍 SearchHistory: popularResponse.data.data:', popularResponse.data?.data);
      // 🔧 API 응답 구조에 맞게 데이터 접근
      const popularData = popularResponse.data?.data || popularResponse.data || [];
      console.log('🔍 SearchHistory: 인기 검색어 데이터 설정:', popularData);
      setPopularSearches(popularData);
      console.log('🔍 SearchHistory: 인기 검색어 상태 업데이트 완료, 길이:', popularData.length);
    } catch (error) {
      console.error('🔍 SearchHistory: 검색 히스토리 로드 실패:', error);
      // 에러가 발생해도 빈 배열로 설정하여 UI가 깨지지 않도록 함
      setRecentSearches([]);
      setPopularSearches([]);
    } finally {
      setIsLoading(false);
      // 🔧 상태 업데이트 후 실제 상태값 확인
      setTimeout(() => {
        console.log('🔍 SearchHistory: 상태 업데이트 후 확인 - 최근:', recentSearches.length, '인기:', popularSearches.length);
      }, 0);
      console.log('🔍 SearchHistory: 로딩 완료, 최근:', recentSearches.length, '인기:', popularSearches.length);
    }
  };

  // 검색어 삭제
  const handleDeleteSearch = async (searchId) => {
    try {
      await searchHistoryAPI.delete(searchId);
      // 삭제 후 목록 새로고침
      loadSearchHistory();
    } catch (error) {
      console.error('검색어 삭제 실패:', error);
    }
  };

  // 검색어 선택
  const handleSearchSelect = (searchTerm) => {
    onSearchSelect(searchTerm);
  };

  // 컴포넌트 마운트 시 검색 히스토리 로드
  useEffect(() => {
    loadSearchHistory();
  }, [cemeteryId, maxRecent, maxPopular]); // 🔧 의존성 배열에 필요한 값들 추가

  if (isLoading) {
    console.log('🔍 SearchHistory: 로딩 중 렌더링');
    return (
      <div className={`search-history ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // 🔧 상태 안전성 확인
  const safeRecentSearches = Array.isArray(recentSearches) ? recentSearches : [];
  const safePopularSearches = Array.isArray(popularSearches) ? popularSearches : [];

  console.log('🔍 SearchHistory: 메인 렌더링, 최근:', safeRecentSearches.length, '인기:', safePopularSearches.length);
  console.log('🔍 SearchHistory: 최근 검색어 데이터:', safeRecentSearches);
  console.log('🔍 SearchHistory: 인기 검색어 데이터:', safePopularSearches);

  return (
    <div className={`search-history ${className}`}>
      {/* 🔧 상단 여백 추가 */}
      <div className="pt-2">
        {/* 최근 검색어 */}
        {safeRecentSearches.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center mb-3 pl-1"> {/* 🔧 pl-1 추가로 왼쪽 여백 확보 */}
              <Clock size={16} className="text-gray-500 mr-2" />
              <h3 className="text-sm font-medium text-gray-700">최근 검색어</h3>
            </div>
            <div className="flex flex-wrap gap-2 pl-1"> {/* 🔧 pl-1 추가로 왼쪽 간격 확보 */}
              {safeRecentSearches.map((search) => (
                <div
                  key={search.id}
                  className="flex items-center bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1 text-sm cursor-pointer group"
                >
                  <button
                    onClick={() => handleSearchSelect(search.search_keyword)}
                    className="flex-1 text-left text-gray-700 hover:text-gray-900"
                  >
                    {search.search_keyword}
                  </button>
                  <button
                    onClick={() => handleDeleteSearch(search.id)}
                    className="ml-2 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* 검색 히스토리가 없는 경우 */}
        {safeRecentSearches.length === 0 && safePopularSearches.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <Search size={24} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">검색 히스토리가 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchHistory; 